/* Huffy favorites (wishlist): saved product handles live in this browser's localStorage.
   <huffy-wishlist-button data-handle>  heart toggle on product cards
   <huffy-wishlist-count>               header badge
   <huffy-wishlist-page>                /pages/wishlist grid */
(() => {
  if (window.HuffyWishlist) return;
  const KEY = 'huffy-wishlist';
  const EVENT = 'huffy:wishlist-change';

  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter((i) => i && typeof i.h === 'string') : [];
    } catch (e) {
      return [];
    }
  };
  let items = read();
  const write = () => {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch (e) { /* private mode: keep in memory */ }
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { handles: items.map((i) => i.h) } }));
  };

  const api = {
    handles: () => items.map((i) => i.h),
    has: (h) => items.some((i) => i.h === h),
    add(h) { if (!api.has(h)) { items.unshift({ h, t: Date.now() }); write(); } },
    remove(h) { const n = items.length; items = items.filter((i) => i.h !== h); if (items.length !== n) write(); },
    toggle(h) { api.has(h) ? api.remove(h) : api.add(h); return api.has(h); },
  };
  window.HuffyWishlist = api;

  // other tabs
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    items = read();
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { handles: api.handles() } }));
  });

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.onClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        api.toggle(this.dataset.handle);
      };
      this.onChange = () => this.sync();
      this.button.addEventListener('click', this.onClick);
      document.addEventListener(EVENT, this.onChange);
      this.sync();
    }
    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener(EVENT, this.onChange);
    }
    sync() {
      const on = api.has(this.dataset.handle);
      const name = this.dataset.title || 'product';
      this.button.setAttribute('aria-pressed', String(on));
      this.button.setAttribute('aria-label', on ? `Remove ${name} from favorites` : `Save ${name} to favorites`);
      this.button.title = on ? 'Saved to favorites' : 'Save to favorites';
    }
  }

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.onChange = () => this.sync();
      document.addEventListener(EVENT, this.onChange);
      this.sync();
    }
    disconnectedCallback() { document.removeEventListener(EVENT, this.onChange); }
    sync() {
      const n = items.length;
      this.textContent = n > 99 ? '99+' : String(n);
      this.hidden = n === 0;
      this.closest('a')?.setAttribute('aria-label', n ? `Favorites (${n} saved)` : 'Favorites');
    }
  }

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const heart = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.5s-7.5-4.6-9.2-9.4C1.6 7.6 3.8 4 7.3 4c2 0 3.6 1.1 4.7 2.7C13.1 5.1 14.7 4 16.7 4c3.5 0 5.7 3.6 4.5 7.1-1.7 4.8-9.2 9.4-9.2 9.4z"/></svg>';

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.countEl = this.querySelector('[data-wishlist-count]');
      this.cache = new Map();
      const locale = document.documentElement.lang || 'en-US';
      const currency = window.Shopify?.currency?.active || 'USD';
      this.money = new Intl.NumberFormat(locale, { style: 'currency', currency });
      this.onChange = () => this.render();
      document.addEventListener(EVENT, this.onChange);
      this.render();
    }
    disconnectedCallback() { document.removeEventListener(EVENT, this.onChange); }

    async load(h) {
      if (!this.cache.has(h)) {
        const root = window.Shopify?.routes?.root || '/';
        this.cache.set(h, fetch(`${root}products/${encodeURIComponent(h)}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => undefined));
      }
      return this.cache.get(h);
    }

    async render() {
      const handles = api.handles();
      const token = (this.token = Symbol());
      const products = await Promise.all(handles.map((h) => this.load(h)));
      if (token !== this.token) return;
      // drop products that no longer exist (404); keep ones that failed on the network
      handles.forEach((h, i) => { if (products[i] === null) api.remove(h); });
      const list = products.filter(Boolean);
      this.empty.hidden = list.length > 0;
      this.grid.hidden = list.length === 0;
      if (this.countEl) this.countEl.textContent = list.length ? `${list.length} saved ${list.length === 1 ? 'item' : 'items'}` : '';
      this.grid.innerHTML = list.map((p) => this.card(p)).join('');
    }

    card(p) {
      const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=600` : '';
      const sale = p.compare_at_price > p.price;
      return `<li class="huffy-wishlist__item">
        <huffy-wishlist-button class="huffy-wl" data-handle="${esc(p.handle)}" data-title="${esc(p.title)}"><button type="button" class="huffy-wl__btn" aria-pressed="true">${heart}</button></huffy-wishlist-button>
        <a class="huffy-wishlist__media" href="${esc(p.url)}">${img ? `<img src="${esc(img)}" alt="${esc(p.title)}" loading="lazy" width="600" height="600">` : ''}</a>
        <a class="huffy-wishlist__title" href="${esc(p.url)}">${esc(p.title)}</a>
        <p class="huffy-wishlist__price"><span>${this.money.format(p.price / 100)}</span>${sale ? ` <s>${this.money.format(p.compare_at_price / 100)}</s>` : ''}</p>
        ${p.available ? '' : '<p class="huffy-wishlist__soldout">Sold out</p>'}
        <a class="huffy-wishlist__cta" href="${esc(p.url)}">View product</a>
      </li>`;
    }
  }

  customElements.get('huffy-wishlist-button') || customElements.define('huffy-wishlist-button', WishlistButton);
  customElements.get('huffy-wishlist-count') || customElements.define('huffy-wishlist-count', WishlistCount);
  customElements.get('huffy-wishlist-page') || customElements.define('huffy-wishlist-page', WishlistPage);
})();
